import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  History,
  Loader2,
  Network,
  Sparkles,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import Tooltip from '../components/ui/Tooltip';
import { chatbotApi } from '../features/chatbot/chatbotApi';
import { useChatbotSession } from '../features/chatbot/useChatbotSession';
import { useConversationHistory } from '../features/chatbot/useConversationHistory';
import { ChatResponse } from '../features/chatbot/types';
import ChatMessage from '../features/chatbot/components/ChatMessage';
import UserMessage from '../features/chatbot/components/UserMessage';
import PendingMessage from '../features/chatbot/components/PendingMessage';
import ErrorMessage from '../features/chatbot/components/ErrorMessage';
import ComposerInput from '../features/chatbot/components/ComposerInput';
import EmptyState from '../features/chatbot/components/EmptyState';
import EvidencePanel from '../features/chatbot/components/EvidencePanel';
import ConversationSidebar from '../features/chatbot/components/ConversationSidebar';

export default function ChatbotPage() {
  const history = useConversationHistory();
  const { messages, sending, loading, sessionId, send, newConversation, openConversation } =
    useChatbotSession({ onTurnPersisted: history.refresh });
  const [input, setInput] = useState('');
  const [evidenceMsgId, setEvidenceMsgId] = useState<string | null>(null);
  const [evidenceHighlight, setEvidenceHighlight] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false); // mobile drawer
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data: health, isError: healthError } = useQuery({
    queryKey: ['chatbot', 'health'],
    queryFn: chatbotApi.getHealth,
    staleTime: 60_000,
    retry: 1,
  });

  const ready = !healthError && (health?.components.ready ?? true);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  // Find the message we're showing evidence for, keep the panel in sync when
  // a new message arrives.
  const evidenceMessage = useMemo<ChatResponse | null>(() => {
    if (!evidenceMsgId) return null;
    const m = messages.find((msg) => msg.id === evidenceMsgId);
    return m && m.kind === 'assistant' ? m.data : null;
  }, [evidenceMsgId, messages]);

  const closeEvidence = () => {
    setEvidenceMsgId(null);
    setEvidenceHighlight(null);
  };

  const handleSend = (text?: string, selectedOption?: string) => {
    const t = (text ?? input).trim();
    if (!t || sending || !ready) return;
    setInput('');
    void send(t, selectedOption);
  };

  const handleViewEvidence = (messageId: string, highlightNodeId?: string | null) => {
    if (evidenceMsgId === messageId && !highlightNodeId) {
      closeEvidence();
    } else {
      setEvidenceMsgId(messageId);
      setEvidenceHighlight(highlightNodeId ?? null);
    }
  };

  const handleNewConversation = () => {
    newConversation();
    closeEvidence();
    setHistoryOpen(false);
  };

  const handleSelectConversation = (id: string) => {
    // Re-selecting the already-open conversation is a no-op only when its
    // transcript is already loaded; if it's empty (e.g. after a reload) fall
    // through and hydrate it from the server. (#1)
    if (id === sessionId && messages.length > 0) {
      setHistoryOpen(false);
      return;
    }
    closeEvidence();
    setHistoryOpen(false);
    void openConversation(id);
  };

  const handleRename = (id: string, title: string) => {
    history.rename(id, title).catch(() => toast.error('Could not rename conversation'));
  };

  const handleDelete = (id: string) => {
    history
      .remove(id)
      .then(() => {
        toast.success('Conversation deleted');
        // If we deleted the open conversation, drop back to a fresh one.
        if (id === sessionId) handleNewConversation();
      })
      .catch(() => toast.error('Could not delete conversation'));
  };

  const hasMessages = messages.length > 0;

  const sidebar = (
    <ConversationSidebar
      conversations={history.conversations}
      activeSessionId={sessionId}
      authed={history.authed}
      isLoading={history.isLoading}
      isError={history.isError}
      authExpired={history.authExpired}
      onRetry={history.retry}
      onNewChat={handleNewConversation}
      onSelect={handleSelectConversation}
      onRename={handleRename}
      onDelete={handleDelete}
    />
  );

  return (
    <div className="h-[calc(100vh-4rem)] flex overflow-hidden bg-neutral-50">
      {/* History sidebar (desktop) */}
      <div className="hidden lg:flex flex-shrink-0">{sidebar}</div>

      {/* History drawer (mobile) */}
      {historyOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="flex-shrink-0 h-full shadow-2xl">{sidebar}</div>
          <div className="flex-1 bg-black/40" onClick={() => setHistoryOpen(false)}>
            <button
              type="button"
              className="absolute top-3 right-3 p-2 rounded-full bg-white/90 text-neutral-700"
              onClick={() => setHistoryOpen(false)}
              aria-label="Close history"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Main pane */}
      <div className="flex-1 min-w-0 min-h-0 flex flex-col">
        {/* Header */}
        <header className="flex-shrink-0 border-b border-neutral-200 bg-white px-6 h-14 flex items-center">
          <div className="flex items-center justify-between gap-3 w-full">
            <div className="flex items-center gap-3 min-w-0">
              <button
                type="button"
                onClick={() => setHistoryOpen(true)}
                className="lg:hidden p-2 -ml-2 rounded-lg text-neutral-600 hover:bg-neutral-100"
                aria-label="Open conversation history"
              >
                <History className="w-5 h-5" />
              </button>
              <div className="w-9 h-9 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <Network className="w-[18px] h-[18px] text-white" />
              </div>
              <div className="min-w-0">
                <h1 className="text-base font-semibold text-neutral-900 leading-tight truncate">
                  Graph Assistant
                </h1>
                <p className="hidden sm:block text-xs text-neutral-500 leading-tight truncate">
                  Cited answers from your knowledge graph — no open-web sources.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {health && (
                <Tooltip
                  content={`neo4j: ${health.components.neo4j ? 'ok' : 'down'} · llm: ${
                    health.components.llm_configured ? 'configured' : 'not configured'
                  }`}
                >
                  <span
                    className={`hidden md:inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${
                      ready
                        ? 'bg-green-50 text-green-700 border-green-200'
                        : 'bg-amber-50 text-amber-800 border-amber-200'
                    }`}
                  >
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${
                        ready ? 'bg-green-500' : 'bg-amber-500'
                      }`}
                    />
                    {ready ? 'Ready' : health.status}
                  </span>
                </Tooltip>
              )}
            </div>
          </div>
        </header>

        {/* Degraded banner */}
        {!ready && (
          <div className="flex-shrink-0 bg-amber-50 border-b border-amber-200 px-6 py-2 text-amber-900 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>Assistant temporarily unavailable. You can browse but messages won't send.</span>
          </div>
        )}

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-4 md:px-6 py-8">
          <div className="max-w-3xl mx-auto space-y-6">
            {loading && !hasMessages ? (
              <div className="flex items-center justify-center py-16 text-neutral-500 gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-sm">Loading conversation…</span>
              </div>
            ) : !hasMessages ? (
              <EmptyState onPromptClick={(p) => handleSend(p)} disabled={!ready} />
            ) : (
              messages.map((m) => {
                if (m.kind === 'user') return <UserMessage key={m.id} text={m.text} />;
                if (m.kind === 'pending') return <PendingMessage key={m.id} />;
                if (m.kind === 'error')
                  return <ErrorMessage key={m.id} text={m.text} onRetry={m.retry} />;
                return (
                  <ChatMessage
                    key={m.id}
                    messageId={m.id}
                    data={m.data}
                    busy={sending}
                    isEvidenceOpen={evidenceMsgId === m.id}
                    onClarificationSelect={(opt) => handleSend(opt, opt)}
                    onFollowUpClick={(text) => handleSend(text)}
                    onViewEvidence={handleViewEvidence}
                  />
                );
              })
            )}
          </div>
        </div>

        {/* Composer */}
        <ComposerInput
          value={input}
          onChange={setInput}
          onSend={() => handleSend()}
          disabled={sending || !ready}
          placeholder={
            ready
              ? 'Ask a question about your knowledge graph…'
              : 'Assistant is unavailable.'
          }
        />
      </div>

      {/* Evidence pane (desktop) */}
      {evidenceMessage && (
        <div className="hidden lg:flex w-[400px] xl:w-[460px] flex-shrink-0 min-w-0 h-full overflow-hidden">
          <EvidencePanel
            evidence={evidenceMessage.evidence}
            highlightNodeId={evidenceHighlight}
            onClose={closeEvidence}
          />
        </div>
      )}

      {/* Evidence sheet (mobile) */}
      {evidenceMessage && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/40"
          onClick={closeEvidence}
        >
          <div
            className="absolute inset-x-0 bottom-0 top-24 bg-white rounded-t-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <EvidencePanel
              evidence={evidenceMessage.evidence}
              highlightNodeId={evidenceHighlight}
              onClose={closeEvidence}
            />
          </div>
        </div>
      )}

      {/* Tiny corner branding on mobile when no messages */}
      {!hasMessages && (
        <div className="lg:hidden fixed bottom-20 right-3 opacity-30 pointer-events-none">
          <Sparkles className="w-4 h-4 text-primary-600" />
        </div>
      )}
    </div>
  );
}
